import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { Button, Flex, notification } from "antd"
import { CheckCircleFilled, CloseOutlined } from "@ant-design/icons"
import { buildGroupRow, countGuestsIn, countGuestsOut, guestWaiversComplete, GUEST_STATE } from "./checkInUtils"

// Green success toast styling (matches the Figma): green surface, white text/icon/actions.
const TOAST_STYLE = { background: "var(--color-success)", borderRadius: 8 }
const TOAST_WHITE = { color: "var(--color-white)" }

// Shared per-guest check-in / check-out flow for private bookings. The operator toggles
// individual guests (pending → in → out → reset), so the flow owns per-guest optimistic
// state, the group modal target, the incomplete-documents gate, and the "N guest checked-in/
// out + Undo" toast.
//
// `thunks` is { checkIn, checkOut, reset, resend }:
//   checkIn(id, guestIds, { override })  → mark guests IN (override bypasses the docs gate)
//   checkOut(id, guestIds)               → mark guests OUT
//   reset(id, guestIds)                  → mark guests PENDING (undo / reset)
//   resend(id, { guestId, documentId })  → resend one unsigned document to one guest
// Each must return a dispatchable promise.
// `onSuccess` (optional) runs after any successful mutation — e.g. to refetch a slice the
// reducer doesn't patch (the Action Queue / upcoming detail).
export const useCheckInFlow = (thunks, onSuccess) => {
  const dispatch = useDispatch()
  const { checkIn, checkOut, reset: resetThunk, resend } = thunks
  // itemId -> { [guestId]: "pending" | "in" | "out" }  (optimistic overrides)
  const [overrides, setOverrides] = useState({})
  // The booking whose per-guest modal is open (group bookings only).
  const [modalItem, setModalItem] = useState(null)
  // A single guest awaiting an incomplete-documents override: { item, guest }.
  const [incompleteTarget, setIncompleteTarget] = useState(null)
  const [isResending, setIsResending] = useState(false)

  // Override-aware per-guest list for a row (falls back to synthetic guests from the counts).
  const getGuests = useCallback(
    (item) => {
      if (!item) return []
      const base = item.guests?.length
        ? item.guests
        : buildGroupRow({
            id: item.id,
            name: item.name,
            guestCount: item.guestCount ?? item.totalGuests ?? 0,
            state: item.checkInState,
            checkedIn: item.checkedInCount ?? 0,
            checkedOut: item.checkedOutCount ?? 0,
            docsSigned: item.docsSign ?? 0,
            docsTotal: item.docsTotal ?? 0,
            documents: item.documents
          })
      const override = overrides[item.id]
      if (!override) return base
      return base.map((guest) => (override[guest.id] ? { ...guest, state: override[guest.id] } : guest))
    },
    [overrides]
  )

  const getCheckedInCount = useCallback((item) => countGuestsIn(getGuests(item)), [getGuests])
  const getCheckedOutCount = useCallback((item) => countGuestsOut(getGuests(item)), [getGuests])

  const setGuestOverride = (itemId, guestId, state) =>
    setOverrides((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [guestId]: state } }))

  // Move one guest to `toState` optimistically, dispatch the matching thunk, and (for check-in/
  // out) show an Undo toast. Rolls the override back if the request fails.
  const setGuestState = useCallback(
    (item, guestId, fromState, toState, { override = false, toast = true } = {}) => {
      setGuestOverride(item.id, guestId, toState)
      let call
      if (toState === GUEST_STATE.IN) call = checkIn(item.id, { override })
      else if (toState === GUEST_STATE.OUT) call = checkOut(item.id)
      else call = resetThunk(item.id)

      const undo = (key) => {
        setGuestState(item, guestId, toState, fromState, { override: true, toast: false })
        notification.destroy(key)
      }

      dispatch(call)
        .then(() => {
          onSuccess?.()
          if (!toast) return
          const count = item.guestCount ?? 1
          const label =
            toState === GUEST_STATE.PENDING
              ? "Reservation status reset"
              : `${count} guest${count === 1 ? "" : "s"} ${toState === GUEST_STATE.IN ? "checked-in" : "checked-out"}`
          const key = `check-${toState}-${item.id}-${guestId}-${Date.now()}`
          notification.open({
            key,
            placement: "topRight",
            style: TOAST_STYLE,
            icon: <CheckCircleFilled style={TOAST_WHITE} />,
            closeIcon: <CloseOutlined style={TOAST_WHITE} />,
            // Keep the label and Undo on a single line (icon left, close top-right handled by antd).
            message: (
              <Flex align="center" justify="space-between" gap={16} style={TOAST_WHITE}>
                <span>{label}</span>
                <Button type="link" size="small" style={TOAST_WHITE} onClick={() => undo(key)}>
                  Undo
                </Button>
              </Flex>
            )
          })
        })
        .catch(() => setGuestOverride(item.id, guestId, fromState))
    },
    [dispatch, checkIn, checkOut, resetThunk, onSuccess]
  )

  // Check a single guest in, gating on their required documents (opens the incomplete modal).
  const checkInGuest = useCallback(
    (item, guest) => {
      if (guestWaiversComplete(guest)) setGuestState(item, guest.id, guest.state, GUEST_STATE.IN)
      else setIncompleteTarget({ item, guest })
    },
    [setGuestState]
  )

  // Toggle one guest from the modal: pending → in (gated), in → out, out → pending (reset).
  const toggleGuest = useCallback(
    (item, guest) => {
      if (guest.state === GUEST_STATE.PENDING) checkInGuest(item, guest)
      else if (guest.state === GUEST_STATE.IN) setGuestState(item, guest.id, guest.state, GUEST_STATE.OUT)
      else setGuestState(item, guest.id, guest.state, GUEST_STATE.PENDING)
    },
    [checkInGuest, setGuestState]
  )

  // Badge click always opens the group modal — the single group row inside handles the
  // check-in (docs-gated), check-out, and reset. Same handler for every badge state.
  const openModal = useCallback((item) => setModalItem(item), [])
  const openCheckIn = openModal
  const openCheckOut = openModal
  const reset = openModal

  // Confirm "Override & Check-in" from the incomplete-documents modal.
  const handleOverride = useCallback(() => {
    if (!incompleteTarget) return
    const { item, guest } = incompleteTarget
    setIncompleteTarget(null)
    setGuestState(item, guest.id, guest.state, GUEST_STATE.IN, { override: true })
  }, [incompleteTarget, setGuestState])

  // Resend one unsigned document to one guest (Resend link in the modal).
  const resendDocument = useCallback(
    (item, guest, document) => {
      if (!resend || isResending) return
      setIsResending(true)
      dispatch(resend(item.id, { documentId: document.id }))
        .catch(() => {})
        .finally(() => setIsResending(false))
    },
    [dispatch, resend, isResending]
  )

  const closeModal = useCallback(() => setModalItem(null), [])
  const closeIncomplete = useCallback(() => setIncompleteTarget(null), [])

  return {
    getGuests,
    getCheckedInCount,
    getCheckedOutCount,
    openCheckIn,
    openCheckOut,
    reset,
    toggleGuest,
    resendDocument,
    handleOverride,
    isResending,
    modalItem,
    incompleteTarget,
    closeModal,
    closeIncomplete
  }
}
